import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, ArrowUpRight, Globe, Sun, Moon } from "lucide-react";
import { NETWORK_SITES, GLOBAL_COUNTRIES, LOGO_URL } from "@/lib/data";
import { PoweredBySocialPulse } from "@/components/site/SocialPulseLink";
import NetworkStrip from "@/components/site/NetworkStrip";
import NavSearch from "@/components/site/NavSearch";
import { useAuth } from "@/lib/AuthContext";
import { useTheme } from "@/lib/ThemeContext";

/** Small header auth cluster — reflects sign-in state on every public page. */
function AuthLinks() {
  const { user, logout } = useAuth();
  if (user) {
    return (
      <>
        <Link to="/app" data-testid="auth-dashboard-button" className="link-underline text-[color:var(--sepia)]">
          Dashboard
        </Link>
        <button onClick={logout} data-testid="auth-logout-button" className="btn-ghost !py-2 !px-4 !text-[12px]">
          Sign out
        </button>
      </>
    );
  }
  return (
    <>
      <Link to="/app/login" data-testid="auth-login-button" className="link-underline text-[color:var(--sepia)]">
        Sign in
      </Link>
      <Link to="/app/register" data-testid="auth-signup-button" className="btn-wood !py-2 !px-4 !text-[12px]">
        Begin Your Archive
      </Link>
    </>
  );
}

/** Sun/moon theme toggle — defaults to the visitor's OS preference, remembers manual overrides. */
function ThemeToggle() {
  const { theme, toggle } = useTheme();
  const isDark = theme === "dark";
  return (
    <button
      data-testid="theme-toggle"
      onClick={toggle}
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      title={isDark ? "Switch to light mode" : "Switch to dark mode"}
      className="w-9 h-9 rounded-full flex items-center justify-center border border-[color:var(--ink-line)] text-[color:var(--ink)] hover:bg-[color:var(--parchment-2)] transition-colors"
    >
      {isDark ? <Sun size={15} /> : <Moon size={15} />}
    </button>
  );
}

const primaryLinks = [
  { to: "/", label: "Home", end: true, testid: "nav-home" },
  { to: "/local", label: "Local", testid: "nav-local" },
  { to: "/missing", label: "Missing & Found", testid: "nav-missing" },
  { to: "/disasters", label: "Disasters", testid: "nav-disasters" },
  { to: "/news", label: "The Wire", testid: "nav-news" },
  { to: "/about", label: "About", testid: "nav-about" },
  { to: "/family-trees", label: "Family Trees", testid: "nav-family-trees" },
  { to: "/cemeteries", label: "Cemeteries", testid: "nav-cemeteries" },
  { to: "/category/heritage", label: "Heritage", testid: "nav-heritage" },
  { to: "/culture", label: "Culture", testid: "nav-culture" },
  { to: "/celebrate-a-life", label: "Celebrate a Life", testid: "nav-celebrate-a-life" },
  { to: "/category/memorials", label: "Memorials", testid: "nav-memorials" },
  { to: "/category/genealogy", label: "Genealogy", testid: "nav-genealogy" },
  { to: "/archive", label: "Archive", testid: "nav-archive" },
  { to: "/contact", label: "Contact", testid: "nav-contact" },
];

function GlobalDropdown() {
  const [open, setOpen] = useState(false);
  const regions = Array.from(new Set(GLOBAL_COUNTRIES.map((c) => c.region)));

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        data-testid="nav-global"
        onClick={() => setOpen((o) => !o)}
        className={`nav-pill ${open ? "active" : ""}`}
      >
        Global
        <ChevronDown size={13} className={`ml-1 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className="absolute top-full mt-2 right-0 w-[720px] max-w-[92vw] bg-[color:var(--bone-2)] border border-[color:var(--ink-line)] rounded-2xl z-50 shadow-2xl"
            data-testid="global-dropdown"
          >
            <div className="p-6 grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-6 max-h-[70vh] overflow-y-auto">
              {regions.map((r) => (
                <div key={r}>
                  <div className="overline text-[color:var(--ink-soft)] mb-3">{r}</div>
                  <ul className="space-y-1">
                    {GLOBAL_COUNTRIES.filter((c) => c.region === r).map((c) => (
                      <li key={c.code}>
                        <Link
                          to={`/region/${c.code}`}
                          className="text-[14px] text-[color:var(--ink)] hover:text-[color:var(--rust)] link-underline"
                        >
                          {c.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [mobileGlobalOpen, setMobileGlobalOpen] = useState(false);
  const location = useLocation();

  return (
    <>
      <motion.header
        data-testid="site-nav"
        initial={{ y: -16, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-50 bg-[color:var(--parchment)]"
      >
        {/* Row 0 — network affiliation strip */}
        <NetworkStrip />

        {/* Row 1 — utility bar: logo left, search / language / theme / auth right */}
        <div className="border-b border-[color:var(--ink-line)]">
          <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-4">
            <Link to="/" data-testid="nav-logo" className="flex flex-col items-start shrink-0">
              <img
                src={LOGO_URL}
                alt="FamilyTree.Team — Connecting Generations. Preserving Legacies."
                className="h-14 md:h-[70px] w-auto select-none rounded-md"
                draggable="false"
              />
            </Link>

            <div className="hidden lg:flex items-center gap-5">
              <NavSearch />
              <button
                data-testid="nav-language-toggle"
                onClick={() => window.dispatchEvent(new Event("ft-open-a11y"))}
                className="inline-flex items-center gap-1.5 text-[14px] font-medium text-[color:var(--ink)] hover:text-[color:var(--sepia)] transition-colors"
              >
                <Globe size={16} /> English <ChevronDown size={12} />
              </button>
              <ThemeToggle />
              <span className="w-px h-5 bg-[color:var(--ink-line)]" />
              <AuthLinks />
            </div>

            {/* Mobile toggle */}
            <button
              className="lg:hidden text-[color:var(--ink)]"
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
            >
              <Menu size={22} />
            </button>
          </div>
        </div>

        {/* Row 2 — slim two-row pill navigation, scrolls naturally with the page */}
        <div className="hidden lg:block border-b border-[color:var(--ink-line)] bg-[color:var(--parchment-3)]/50">
          <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-3 flex flex-wrap items-center gap-2.5">
            {primaryLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={l.testid}
                className={({ isActive }) => `nav-pill ${isActive ? "active" : ""}`}
              >
                {l.label}
              </NavLink>
            ))}
            <GlobalDropdown />
          </div>
        </div>
      </motion.header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] bg-[color:var(--parchment)] overflow-y-auto"
            data-testid="mobile-menu"
          >
            <div className="p-6 flex items-center justify-between border-b border-[color:var(--ink-line)] sticky top-0 bg-[color:var(--parchment)] z-10">
              <div className="flex flex-col items-start">
                <img src={LOGO_URL} alt="FamilyTree.Team" className="h-14 w-auto rounded-md" />
                <PoweredBySocialPulse className="mt-0.5 font-sans-ui text-[8px] tracking-[0.14em] uppercase text-[color:var(--sepia-warm)]/75" />
              </div>
              <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" aria-label="Close" className="text-[color:var(--sepia)]">
                <X size={24} />
              </button>
            </div>
            <div className="px-6 pt-4 flex items-center gap-3">
              <ThemeToggle />
            </div>
            <div className="p-8 space-y-3">
              {primaryLinks.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.end}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) => `block nav-pill !text-lg !py-2.5 !px-4 w-fit ${isActive ? "active" : ""}`}
                >
                  {l.label}
                </NavLink>
              ))}

              <button
                type="button"
                onClick={() => setMobileGlobalOpen((v) => !v)}
                className="w-full flex items-center justify-between font-heritage-caps text-xl text-[color:var(--ink)] pt-3"
                data-testid="mobile-global-toggle"
              >
                Global
                <ChevronDown className={`transition-transform duration-300 ${mobileGlobalOpen ? "rotate-180" : ""}`} size={20} />
              </button>
              {mobileGlobalOpen && (
                <div className="pl-2 grid grid-cols-2 gap-x-4 gap-y-2 pb-4">
                  {GLOBAL_COUNTRIES.map((c) => (
                    <Link
                      key={c.code}
                      to={`/region/${c.code}`}
                      onClick={() => setOpen(false)}
                      className="text-[15px] link-underline text-[color:var(--ink)]"
                    >
                      {c.name}
                    </Link>
                  ))}
                </div>
              )}

              <div className="pt-6 border-t border-[color:var(--ink-line)]">
                <div className="overline text-[color:var(--ink-soft)] mb-3">The Pulse Network</div>
                <div className="flex flex-col gap-3">
                  {NETWORK_SITES.map((s) => (
                    <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between text-[16px] link-underline">
                      {s.label}
                      <ArrowUpRight size={16} />
                    </a>
                  ))}
                </div>
              </div>

              <div className="pt-6 border-t border-[color:var(--ink-line)] flex gap-3">
                <Link to="/app/login" onClick={() => setOpen(false)} data-testid="mobile-auth-login" className="btn-ghost">
                  Sign in
                </Link>
                <Link to="/app/register" onClick={() => setOpen(false)} data-testid="mobile-auth-signup" className="btn-wood">
                  Begin archive
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
